/**
 * LLM Service for generating statistical analysis narratives
 *
 * This service integrates with Claude API to generate natural language
 * summaries based on structured statistical data.
 */

import { getLLMApiKey, getLLMProvider } from '../utils/secureStorage';
import type { StatisticalAnalysisInput } from './statisticalAnalysisFramework';
import { LLM_PROMPT_TEMPLATE } from './statisticalAnalysisFramework';

const isDevelopment = process.env.NODE_ENV === 'development';

// Use proxy for all LLM calls to avoid CORS issues
// In development, setupProxy.js forwards /api/* to http://localhost:3001
// In production, Cloudflare Pages Function handles /api/llm-proxy
const LLM_PROXY_URL = '/api/llm-proxy';

export interface LLMConfig {
  provider: 'anthropic' | 'openai' | 'google';
  model?: string;
  maxTokens?: number;
}

const DEFAULT_CONFIG: LLMConfig = {
  provider: 'anthropic',
  model: 'claude-3-5-haiku-20241022',
  maxTokens: 4096
};

/**
 * Get default model for a given provider
 */
function getDefaultModel(provider: 'anthropic' | 'openai' | 'google'): string {
  switch (provider) {
    case 'anthropic':
      return 'claude-3-5-haiku-20241022';
    case 'openai':
      return 'gpt-4o-mini-2024-07-18';
    case 'google':
      return 'gemini-3-flash-preview';
    default:
      return 'claude-3-5-haiku-20241022';
  }
}

/**
 * Generate narrative using LLM proxy (unified for all providers)
 */
async function generateWithProxy(
  analysisData: StatisticalAnalysisInput,
  apiKey: string,
  config: LLMConfig
): Promise<string> {
  const prompt = LLM_PROMPT_TEMPLATE.replace(
    '{JSON-INPUT}',
    JSON.stringify(analysisData, null, 2)
  );

  let response: Response;

  try {
    response = await fetch(LLM_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        provider: config.provider,
        apiKey,
        model: config.model,
        prompt,
        maxTokens: config.maxTokens || DEFAULT_CONFIG.maxTokens
      })
    });
  } catch (fetchError) {
    if (isDevelopment) {
      throw new Error(
        'Kan inte ansluta till proxy-servern. Starta den med: npm run proxy\n\n' +
        'Du behöver två terminaler:\n' +
        '  Terminal 1: npm start\n' +
        '  Terminal 2: npm run proxy'
      );
    }
    throw new Error('Kan inte ansluta till API-proxy. Kontakta support.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(`LLM API error: ${error.error?.message || error.message || response.statusText}`);
  }

  const data = await response.json();

  // Extract text based on provider response format
  if (config.provider === 'anthropic') {
    return data.content[0].text;
  } else if (config.provider === 'openai') {
    return data.choices[0].message.content;
  } else if (config.provider === 'google') {
    return data.candidates[0].content.parts[0].text;
  }

  throw new Error('Unexpected response format from LLM proxy');
}

/**
 * Main function to generate narrative
 *
 * @param analysisData - Structured analysis data from the framework
 * @param config - Optional LLM configuration (provider, model, etc.)
 * @returns AI-generated narrative or null if no API key is configured
 */
export async function generateNarrative(
  analysisData: StatisticalAnalysisInput,
  config: Partial<LLMConfig> = {}
): Promise<string | null> {
  const apiKey = getLLMApiKey();

  // If no API key is configured, return null (caller can fall back to template)
  if (!apiKey) {
    if (isDevelopment) console.warn('[LLM] No API key configured - narrative generation skipped');
    return null;
  }

  // Get stored provider, fall back to config or default
  const storedProvider = getLLMProvider();
  const finalProvider = storedProvider || config.provider || DEFAULT_CONFIG.provider;
  const fullConfig: LLMConfig = {
    provider: finalProvider,
    model: config.model || getDefaultModel(finalProvider),
    maxTokens: config.maxTokens || DEFAULT_CONFIG.maxTokens
  };

  try {
    if (isDevelopment) {
      console.log('[LLM] Stored provider:', storedProvider);
      console.log('[LLM] Config provider:', config.provider);
      console.log('[LLM] Final config:', fullConfig);
      console.log('[LLM] Generating narrative using:', fullConfig.provider, fullConfig.model);
    }

    return await generateWithProxy(analysisData, apiKey, fullConfig);
  } catch (error) {
    console.error('[LLM] Error generating narrative:', error);
    throw error;
  }
}

/**
 * Test API key by making a simple request via proxy
 */
export async function testApiKey(
  apiKey: string,
  provider: 'anthropic' | 'openai' | 'google'
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(LLM_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        provider,
        apiKey,
        prompt: 'Test',
        maxTokens: 10
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      return {
        success: false,
        error: error.error?.message || error.message || 'Invalid API key'
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error'
    };
  }
}
